import React, { useEffect, useState } from "react";
import { api, getErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowSquareOut, Warning, Images, Plus, Trash } from "@phosphor-icons/react";

const Card = ({ title, children, testid }) => (
  <div className="border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="font-display text-base font-black tracking-tight">{title}</div>
    <div className="mt-4">{children}</div>
  </div>
);

const STATUS = {
  new: "bg-[#0044FF] text-white",
  contacted: "bg-[#F59E0B] text-white",
  won: "bg-[#22C55E] text-white",
  lost: "bg-[#9CA3AF] text-white",
  dismissed: "bg-[#F3F4F6] text-[#4B5563]",
};

export default function AdminJunkRemoval() {
  const [data, setData] = useState(null);
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: ov } = await api.get("/junk-removal/admin/overview");
      setData(ov);
      setC(ov.config);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/junk-removal/admin/config", c);
      setC(res.config);
      toast.success("Saved — live on /junk-removal immediately.");
    } catch (e) {
      toast.error(getErr(e));
    }
    setSaving(false);
  };

  if (!data || !c) return <div className="px-5 py-10 text-sm text-[#4B5563]">Loading…</div>;
  const an = data.analytics || {};
  const reqs = data.requests || [];
  const photoRate = an.form_complete ? Math.round(((an.photo_upload || 0) / an.form_complete) * 100) : 0;
  const setList = (key, i, k, v) => {
    const rows = [...(c[key] || [])];
    rows[i] = { ...rows[i], [k]: v };
    setC({ ...c, [key]: rows });
  };
  const addRow = (key, blank) => setC({ ...c, [key]: [...(c[key] || []), blank] });
  const delRow = (key, i) => setC({ ...c, [key]: (c[key] || []).filter((_, j) => j !== i) });

  return (
    <div className="px-5 py-8 md:px-10 md:py-10" data-testid="admin-junk-removal-page">
      <div className="font-mono-label">Junk Removal Funnel</div>
      <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">Junk Removal</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
        Live at{" "}
        <a className="font-bold text-[#0044FF]" href="/junk-removal" target="_blank" rel="noreferrer">
          hcobnetwork.com/junk-removal <ArrowSquareOut className="inline" size={12} />
        </a>
        . Published rate card, photo-to-quote (no instant calculator by design). Requests land in the Quotes inbox tagged JUNK REMOVAL with photos attached. Client contact is EMAIL ONLY until a client-facing A2P campaign is registered — consent is captured regardless.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5 lg:grid-cols-9">
        {[
          ["Page views", an.page_view || 0],
          ["Pricing views", an.pricing_view || 0],
          ["Form starts", an.form_start || 0],
          ["Completions", an.form_complete || 0],
          ["Photo-upload rate", `${photoRate}%`],
          ["Call clicks", an.call_click || 0],
          ["Text clicks", an.text_click || 0],
          ["→ Fresh Start", an.xlink_fresh_start || 0],
          ["→ Deep Cleaning", an.xlink_deep_cleaning || 0],
        ].map(([label, n]) => (
          <div key={label} className="border border-[#E5E7EB] bg-white p-4" data-testid={`jr-stat-${label}`}>
            <div className="font-display text-2xl font-black">{n}</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">{label}</div>
          </div>
        ))}
      </div>
      {an.form_complete >= 10 && photoRate < 40 && (
        <div className="mt-2 inline-flex items-center gap-1 bg-[#FEF2F2] px-2 py-1 text-[11px] font-bold text-[#B91C1C]" data-testid="jr-photo-rate-flag">
          <Warning size={12} weight="fill" /> Photo-upload rate is running low ({photoRate}%) — the photo ask on the page needs reframing.
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Rate card — load sizes" testid="jr-tiers-card">
          <p className="text-sm text-[#4B5563]">Label · what it looks like · price · truck fill % (drives the graphic).</p>
          {(c.load_tiers || []).map((t, i) => (
            <div key={t.key || i} className="mt-3 grid grid-cols-[1fr_1.4fr_90px_64px_28px] items-center gap-2">
              <Input value={t.label} onChange={(e) => setList("load_tiers", i, "label", e.target.value)} data-testid={`jr-tier-label-${i}`} />
              <Input value={t.looks_like} onChange={(e) => setList("load_tiers", i, "looks_like", e.target.value)} data-testid={`jr-tier-looks-${i}`} />
              <Input value={t.price} onChange={(e) => setList("load_tiers", i, "price", e.target.value)} data-testid={`jr-tier-price-${i}`} />
              <Input type="number" min={0} max={100} value={t.fill} onChange={(e) => setList("load_tiers", i, "fill", Number(e.target.value))} data-testid={`jr-tier-fill-${i}`} />
              <button className="text-[#EF4444]" onClick={() => delRow("load_tiers", i)} title="Remove" data-testid={`jr-tier-del-${i}`}><Trash size={14} /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => addRow("load_tiers", { key: `t${Date.now()}`, label: "", looks_like: "", price: "", fill: 50 })} data-testid="jr-tier-add">
            <Plus size={12} /> Add tier
          </Button>
          <div className="mt-4 text-xs font-bold">Hero price anchor</div>
          <Input className="mt-1" value={c.price_anchor || ""} onChange={(e) => setC({ ...c, price_anchor: e.target.value })} data-testid="jr-price-anchor-input" />
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div><div className="text-xs font-bold">Schema low price ($)</div><Input type="number" className="mt-1" value={c.low_price ?? ""} onChange={(e) => setC({ ...c, low_price: Number(e.target.value) })} data-testid="jr-low-price" /></div>
            <div><div className="text-xs font-bold">Schema high price ($)</div><Input type="number" className="mt-1" value={c.high_price ?? ""} onChange={(e) => setC({ ...c, high_price: Number(e.target.value) })} data-testid="jr-high-price" /></div>
          </div>
        </Card>

        <Card title="Rate card — single items" testid="jr-items-card">
          {(c.single_items || []).map((r, i) => (
            <div key={i} className="mt-3 grid grid-cols-[1.4fr_1fr_28px] items-center gap-2">
              <Input value={r.item} onChange={(e) => setList("single_items", i, "item", e.target.value)} data-testid={`jr-item-name-${i}`} />
              <Input value={r.price} onChange={(e) => setList("single_items", i, "price", e.target.value)} data-testid={`jr-item-price-${i}`} />
              <button className="text-[#EF4444]" onClick={() => delRow("single_items", i)} title="Remove" data-testid={`jr-item-del-${i}`}><Trash size={14} /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => addRow("single_items", { item: "", price: "" })} data-testid="jr-item-add">
            <Plus size={12} /> Add item
          </Button>
        </Card>

        <Card title="Baltimore City bulk-trash comparison" testid="jr-city-card">
          <p className="text-sm text-[#4B5563]">Review quarterly. The city column must be correctable without a deploy.</p>
          {(c.city_rows || []).map((r, i) => (
            <div key={i} className="mt-3 grid grid-cols-[1fr_1.2fr_1.2fr_28px] items-center gap-2">
              <Input value={r.label} onChange={(e) => setList("city_rows", i, "label", e.target.value)} data-testid={`jr-city-label-${i}`} />
              <Input value={r.city} onChange={(e) => setList("city_rows", i, "city", e.target.value)} data-testid={`jr-city-city-${i}`} />
              <Input value={r.hcob} onChange={(e) => setList("city_rows", i, "hcob", e.target.value)} data-testid={`jr-city-hcob-${i}`} />
              <button className="text-[#EF4444]" onClick={() => delRow("city_rows", i)} title="Remove" data-testid={`jr-city-del-${i}`}><Trash size={14} /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-3" onClick={() => addRow("city_rows", { label: "", city: "", hcob: "" })} data-testid="jr-city-add">
            <Plus size={12} /> Add row
          </Button>
          <div className="mt-4 text-xs font-bold">"As of" footnote</div>
          <Input className="mt-1" value={c.city_as_of || ""} onChange={(e) => setC({ ...c, city_as_of: e.target.value })} data-testid="jr-city-as-of-input" />
        </Card>

        <Card title="Launch holds (pending Cory's confirmation)" testid="jr-holds-card">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!c.show_same_day} onChange={(e) => setC({ ...c, show_same_day: e.target.checked })} data-testid="jr-toggle-same-day" />
            <span><b>Same-day / next-day pickup FAQ</b><br /><span className="text-[#4B5563]">Turn off if dispatch cannot reliably meet it — the question disappears from the page and the FAQ schema.</span></span>
          </label>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!c.show_donation} onChange={(e) => setC({ ...c, show_donation: e.target.checked })} data-testid="jr-toggle-donation" />
            <span><b>Donation claim in "What happens to my stuff?"</b><br /><span className="text-[#4B5563]">Turn off if there is no donation route; the answer falls back to disposal + recycling only.</span></span>
          </label>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!c.plus_tax} onChange={(e) => setC({ ...c, plus_tax: e.target.checked })} data-testid="jr-toggle-plus-tax" />
            <span><b>Show "plus tax" note under the rate card</b><br /><span className="text-[#4B5563]">Enable once Maryland sales-tax treatment is confirmed.</span></span>
          </label>
          <div className="mt-4 border border-dashed border-[#E5E7EB] bg-[#F9FAFB] p-3 text-[11px] text-[#4B5563]">
            Also pending: the full rate card + $125 published minimum (edit above), and a <b>real job photo</b> for the social share image (currently the branded default card — no stock photography). The words licensed / bonded / insured never appear on this page by design.
          </div>
          <Button className="mt-4" disabled={saving} onClick={save} data-testid="jr-save-config">
            {saving ? "Saving…" : "Save rate card & settings"}
          </Button>
        </Card>
      </div>

      <div className="mt-6">
        <div className="font-display text-lg font-black">Quote requests ({reqs.length} recent)</div>
        <div className="mt-3 overflow-x-auto border border-[#E5E7EB] bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#4B5563]">
                <th className="p-3">When</th><th className="p-3">Client</th><th className="p-3">Scope</th>
                <th className="p-3">Photos</th><th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.request_id} className="border-b border-[#F3F4F6] align-top" data-testid={`jr-req-row-${r.request_id}`}>
                  <td className="p-3 text-xs text-[#4B5563]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="p-3">
                    <div className="font-bold">{r.name}</div>
                    <div className="text-xs text-[#4B5563]">{r.phone}{r.email ? ` · ${r.email}` : ""}</div>
                    <div className="text-xs text-[#4B5563]">{r.location}</div>
                    {r.heavy && (
                      <div className="mt-1 inline-flex items-center gap-1 bg-[#FEF2F2] px-2 py-0.5 text-[10px] font-bold text-[#B91C1C]" data-testid={`jr-req-heavy-${r.request_id}`}>
                        <Warning size={10} weight="fill" /> QUOTE BY WEIGHT / SCOPE
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    <div>{(r.item_labels || []).join(" + ")}</div>
                    <div className="text-[#4B5563]">{r.volume_label} · {r.timeline_label}</div>
                    {r.access_notes && <div className="mt-1 max-w-[320px] text-[#4B5563]">"{r.access_notes}"</div>}
                  </td>
                  <td className="p-3">
                    {(r.photos || []).length ? (
                      <div className="flex flex-wrap gap-1" data-testid={`jr-req-photos-${r.request_id}`}>
                        {r.photos.slice(0, 6).map((p) => (
                          <a key={p} href={`${BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer">
                            <img src={`${BACKEND_URL}/api/files/${p}`} alt="Client photo" className="h-12 w-12 object-cover" loading="lazy" />
                          </a>
                        ))}
                        {r.photos.length > 6 && <span className="self-center text-[11px] text-[#4B5563]">+{r.photos.length - 6}</span>}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#9CA3AF]"><Images size={12} /> none</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-[10px] font-bold tracking-widest ${STATUS[r.status] || STATUS.new}`}>{(r.status || "new").toUpperCase()}</span>
                  </td>
                </tr>
              ))}
              {!reqs.length && <tr><td colSpan={5} className="p-6 text-center text-xs text-[#4B5563]">No quote requests yet — the funnel is live at /junk-removal.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] text-[#4B5563]">Status follows the Quotes inbox (New → Contacted → Won/Lost).</div>
      </div>
    </div>
  );
}
